import { filter, sortBy, reduce, keys, isNil } from "lodash";
import { create } from "zustand";
import { isTrust } from "../utils/investor";
import { ListQuestionType } from "../utils/kycQuestionConstant";
import restUtils from "core/utils/rest";

// Local types
export type QuestionItem = {
  id: string;
  section: string;
  label: string;
  type: string;
  sequence: string | number;
  referenceGroupId: string;
  mandatory: boolean;
};

export type AnswerItem = {
  questionId: string;
  value: any;
};

type StoreState = {
  questions: QuestionItem[];
  answers: AnswerItem[];
  loading: boolean;
  error: unknown;
  getQuestionsOfSection: (sectionName: string) => QuestionItem[];
  fetchData: (investorId: string) => Promise<void>;
};

type Mapping = {
  questionId: string;
  field: string;
  toAnswer?: (fieldValue: any) => any;
  toPayload?: (ansValue: any) => any;
};


const initialState: StoreState = {
  questions: [],
  answers: [],
  loading: true,
  error: null,
  getQuestionsOfSection: () => [],
  fetchData: async () => { /* init */ },
};
export const kycQuestionStoreProvider: (set: (partial: Partial<StoreState>) => void, get: () => StoreState) => StoreState = (set, get) => ({
  questions: [],
  answers: [],
  loading: true,
  error: null,
  getQuestionsOfSection: (sectionName: string) => {
    const questions = get().questions;
    return sortBy(
      filter(questions, (q) => q.section === sectionName),
      q => Number(q.sequence)
    );
  },
  fetchData: async (investorId: string) => {
    set(initialState);
    try {
      const [questions, answers] = await fetchKycQuestions(investorId);
      set({ questions, answers, loading: false });
    } catch (err) {
      console.error(err);
      set({ error: err, loading: false });
    }
  }
});

export const useKycQuestionStore = create<StoreState>(kycQuestionStoreProvider);

interface KycAnswerRow { questionId: string; questionValue: any }
interface KycQuestionRow {
  questionRef: string;
  sectionName: string;
  questionName: string;
  inputType: string;
  sequence: string | number;
  refType: string;
  mandatory: string;
  answers?: KycAnswerRow[];
}
interface KycQuestionsResponse { rows: KycQuestionRow[] }

export function fetchKycQuestions(investorId: string): Promise<[QuestionItem[], AnswerItem[]]> {
  return restUtils.call(
    "PortalApi",
    `hubwise-api/aml/investors/${investorId}/kyc-questions`,
    "GET"
  ).then((response) => {
    const resp = response as KycQuestionsResponse;
    const answers: AnswerItem[] = [];
    const questions: QuestionItem[] = [];
    resp.rows.forEach((el: KycQuestionRow) => {
      const [q, ans] = dataToQuestionAnswers(el);
      questions.push(q);
      answers.push(...ans);
    });
    return [questions, answers];
  });
}

export function updateAndSyncKycAnswers(
  personId: string,
  entityType: string,
  answerObj: Record<string, any>,
  questions: QuestionItem[],
  investorKyc: Record<string, any>,
  trsData: Record<string, any> = {}
) {
  const requests: Promise<any>[] = [updateKycAnswers(personId, answerObj, questions)];
  if (isTrust(entityType)) {
    // sync TRS data
    requests.push(answerSyncWith(personId, answerObj, trsData, trsFieldMapping, syncTrsData));
  }
  // sync KYC data
  answerSyncWith(personId, answerObj, investorKyc, answerKycUpdateMapping, syncKycData);
  return Promise.all(requests);
}

function updateKycAnswers(personId: string, answerObj: Record<string, any>, questions: QuestionItem[]) {
  const newAnswer = answerObjToValueArray(answerObj, questions);
  return restUtils.call(
    "PortalApi",
    `hubwise-api/aml/investors/${personId}/kyc-values`,
    "POST",
    newAnswer.map((ans: AnswerItem) => ({ questionId: ans.questionId, questionValue: ans.value }))
  );
}

function syncTrsData(personId: string, newTrsData: Record<string, any>) {
  return restUtils.call(
    "PortalApi",
    `hubwise-api/aml/investors/${personId}/trs`,
    "POST",
    newTrsData
  );
}

function syncKycData(personId: string, newKycInfo: Record<string, any>, oldKycInfo: Record<string, any>, mappings: Mapping[]) {
  const [payload, dirty] = getKycUpdatePayload(newKycInfo, oldKycInfo, mappings);
  if (!dirty) {
    return Promise.resolve();
  }
  return restUtils.call(
    "PortalApi",
    `hubwise-api/aml/investors/${personId}/kyc`,
    "POST",
    payload
  );
}

function getKycUpdatePayload(newKyc: Record<string, any>, investorKyc: Record<string, any>, mappings: Mapping[]): [Record<string, any>, boolean] {
  const payload: Record<string, any> = {
    ...investorKyc
  };
  let dirty = false;
  // sync kyc information in KYCINVPF and KYCVALPF
  mappings.forEach((mapping) => {
    const newValue = newKyc[mapping.field];
    const oldValue = investorKyc[mapping.field];
    if (newValue !== oldValue) {
      dirty = true;
      payload[mapping.field] = isNil(newValue) ? "" : newValue;
    }
  });
  return [payload, dirty];
}

export function assembleKycAnswerObject(entityType: string, answerArray: AnswerItem[], questions: QuestionItem[], investorKyc: Record<string, any>, trsData: Record<string, any>) {
  const ans = aggregateAnswers(answerArray, questions);
  // merge values in KYCVALPF (category, sub-category)
  mergeIntoAnswers(ans, investorKycToAnswerMapping, investorKyc);
  // merge values in INVTRSPF, mergeIntoAnswers will take care of null investorTrs.
  if (isTrust(entityType)) {
    mergeIntoAnswers(ans, trsFieldMapping, trsData);
  }
  return ans;
}

function aggregateAnswers(answerArray: AnswerItem[], questions: QuestionItem[]) {
  const answerObj: Record<string, any> = answerArray.reduce((acc: Record<string, any>, curr: AnswerItem) => {
    const question = questions.find(el => el.id === curr.questionId);
    // igore value
    if (!question) {
      return acc;
    }
    const isListQuestion = ListQuestionType.includes(question.type);
    if (isListQuestion) {
      acc[curr.questionId] = acc[curr.questionId] || [];
      (acc[curr.questionId] as any[]).push(curr.value);
    } else {
      acc[curr.questionId] = curr.value;
    }
    return acc;
  }, {} as Record<string, any>);
  return answerObj;
}

export function mergeIntoAnswers(answer: Record<string, any>, mappings: Mapping[] = [], object: Record<string, any> = {}) {
  mappings.forEach((mapping: Mapping) => {
    const answerValue = answer[mapping.questionId];
    const fieldValue = object?.[mapping.field];
    if (isNil(answerValue) && fieldValue) {
      answer[mapping.questionId] = mapping.toAnswer ? mapping.toAnswer(fieldValue) : fieldValue;
    }
  });
  // If callee need a object with new reference, just spread it using `...` operator.
  return answer;
}

export function answerSyncWith(personId: string, answer: Record<string, any>, previousObj: Record<string, any>, mappings: Mapping[] = [], syncMethod: (personId: string, newObj: Record<string, any>, previousObj: Record<string, any>, mappings: Mapping[]) => Promise<any>) {
  const newObj: Record<string, any> = {};
  mappings.forEach((mapping: Mapping) => {
    const answerValue = answer[mapping.questionId];
    newObj[mapping.field] = mapping.toPayload ? mapping.toPayload(answerValue) : (answerValue || "");
  });
  return syncMethod(personId, newObj, previousObj, mappings);
}

function answerObjToValueArray(values: Record<string, any>, questions: QuestionItem[]) {
  // remove category and subCategory. They should only store in KYCINVPF
  const ignoreQuestionIds = ["QT1", "QT2"];
  const valueArray: AnswerItem[] = reduce(keys(values), (acc: AnswerItem[], questionId: string) => {
    const answer = values[questionId];
    const question = questions.find((el) => el.id === questionId);
    // igore value
    if (!question) {
      return acc;
    }
    const isListQuestion = ListQuestionType.includes(question.type);
    if (isListQuestion && Array.isArray(answer)) {
      acc.push(...answer.map((v: any) => ({ questionId, value: v })));
    } else {
      acc.push({
        questionId,
        value: answer
      });
    }
    return acc;
  }, [] as AnswerItem[]);
  return valueArray.filter((el) => !ignoreQuestionIds.includes(el.questionId));
}

function dataToQuestionAnswers(row: KycQuestionRow): [QuestionItem, AnswerItem[]] {
  const question: QuestionItem = {
    id: row.questionRef,
    section: row.sectionName,
    label: row.questionName,
    type: row.inputType,
    sequence: row.sequence,
    referenceGroupId: row.refType,
    mandatory: row.mandatory === "Y",
  };
  let answers: AnswerItem[] = [];
  if (row.answers && row.answers.length > 0) {
    answers = row.answers.map((ans: KycAnswerRow) => {
      return {
        questionId: ans.questionId,
        value: ans.questionValue
      };
    });
  }
  return [question, answers];
}

export const answerKycUpdateMapping: Mapping[] = [
  {
    questionId: "QT1",
    field: "categoryCode",
  },
  {
    questionId: "QT2",
    field: "subTypeCode"
  },
  {
    questionId: "Q190",
    field: "countryOfIncorporation",
  },
  {
    questionId: "Q200",
    field: "dateOfIncorporation"
  },
  {
    questionId: "Q220",
    field: "industryCode",
  },
  {
    questionId: "Q225",
    field: "industryText",
  }
];

export const investorKycToAnswerMapping: Mapping[] = [
  {
    questionId: "QT1",
    field: "categoryCode",
  },
  {
    questionId: "QT2",
    field: "subTypeCode"
  }
];

export const trsFieldMapping: Mapping[] = [
  {
    questionId: "Q510A",
    field: "trsStatus",
    toAnswer: (fieldValue) => {
      return fieldValue ? (fieldValue === "REG" ? "Y" : "N") : "";
    },
    toPayload: (ansValue) => {
      return ansValue === "Y" ? "REG" : "";
    }
  },
  {
    questionId: "Q510B",
    field: "trsStatus",
    toAnswer: (fieldValue) => {
      return fieldValue ? (fieldValue === "EXP" ? "Y" : "N") : "";
    },
    toPayload: (ansValue) => {
      return ansValue === "Y" ? "EXP" : "";
    }
  },
  {
    questionId: "Q515",
    field: "trsReference",
  },
  {
    questionId: "Q520",
    field: "exemptReason",
  }
];

export default useKycQuestionStore;
